import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ENV_LIST } from '@app/interfaces/config.interface';

import { Observable } from 'rxjs';
import { catchError, mergeMap, publishLast, refCount } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { BasicObject, ObjectOfString } from '../models/services.interface';
import { BtlPrismeService } from '../services/btl-prisme.service';
import { ConfigurationService } from '../services/configuration.service';
import { CustomHttpParamEncoder } from './custom-http-param-encoder.class';
import { Oauth2Service } from './oauth2.service';

@Injectable({
    providedIn: 'root',
})
export class Oauth2ResourcesService {
    public static HTTP_UNAUTHORIZED: number = 401;

    public inService: boolean = false;
    public store;

    protected forceIE11: boolean = false;
    protected headers: BasicObject = {};
    protected localService: boolean = false;
    protected params: HttpParams;
    protected resourceUrl: string = '';

    constructor(
        protected oauth2: Oauth2Service,
        protected http: HttpClient,
        protected configService: ConfigurationService,
        protected prismeService: BtlPrismeService,
    ) {
        this.params = new HttpParams({ encoder: new CustomHttpParamEncoder() });
    }

    public addHeaders(headers: BasicObject): this {
        const resource: this = this.getInstance();
        resource.headers = Object.assign(resource.headers, headers);
        return resource;
    }

    public adresseLivraison(): this {
        return this.getResource('/adresses-livraison');
    }

    public clickAndCollect(): this {
        return this.getResource('/click-and-collect');
    }

    public pointRelais(): this {
        return this.getResource('/points-relais');
    }

    public adresseFacturation(): this {
        return this.getResource('/adresse-facturation');
    }

    public avantages(): this {
        return this.getResource('/avantages');
    }

    public categories(id?: string): this {
        return this.getResource(`/categories${id ? '/' + id : ''}`);
    }

    public codeInsee(codeInsee: string): this {
        return this.getResource('/' + codeInsee);
    }

    public codeNumeroVoie(codeNumeroVoie: string): this {
        return this.getResource('/' + codeNumeroVoie);
    }

    public codeVoie(codeVoie: string): this {
        return this.getResource('/' + codeVoie);
    }

    public commande(id?: number | string): this {
        let resourceUrl = '/commande';
        if (id) {
            resourceUrl += '/' + id.toString();
        }
        return this.getResource(resourceUrl);
    }

    public compatibiliteSim(): this {
        return this.getResource('/compatibilite-sim');
    }

    public comptesFacturation(id?: string): this {
        const resourceUrl = '/comptes-facturation' + (id ? '/' + id : '');
        return this.getResource(resourceUrl);
    }

    public config(): this {
        return this.getResource('/config');
    }

    public configuration(): this {
        return this.getResource('/configuration');
    }

    public contracts(contractId: number | string): this {
        const resourceUrl = '/contrats/' + contractId;
        return this.getResource(resourceUrl);
    }

    public validationEmail(): this {
        const resource: this = this.getResource('/adresses/validation-email');
        return resource;
    }

    public contratsSignes(contractId?: string): this {
        return this.getResource('/contrats-signes' + (contractId ? '/' + contractId : ''));
    }

    public contratsUtilises(): this {
        return this.getResource('/contrats-utilises');
    }

    public controleSiren(siren: string): this {
        const resourceUrl = '/controleSiren/' + siren;
        return this.getResource(resourceUrl);
    }

    public coordonnees(): this {
        return this.getResource('/coordonnees');
    }

    public coordonneesContact(): this {
        return this.getResource('/coordonnees-contact');
    }

    public creneauxLivraison(): this {
        return this.getResource('/creneaux-livraison');
    }

    public recherche(): this {
        return this.getResource('/recherche');
    }

    public delete<T = any>(): Observable<T> {
        return this.getRequest('DELETE');
    }

    public details(): this {
        return this.getResource('/boutiques/details');
    }

    public documents(id?: string): this {
        const resourceUrl = '/documents' + (id ? '/' + id : '');
        return this.getResource(resourceUrl);
    }

    public eligibilite(idPanier?: string): this {
        const resourceUrl = `/eligibilite${idPanier ? '/' + idPanier : ''}`;
        return this.getResource(resourceUrl);
    }

    public eligibiliteTokyo(): this {
        return this.getResource('/RsGacEligibilite/eligibilite-tokyo');
    }

    public eligibilitesPrevisionnelles(codeInsee: string): this {
        const resource: this = this.getResource('/eligibilites-previsionnelles');
        resource.setParams({ codeInsee });
        return resource;
    }

    public eligibilitesRenouvellement(): this {
        return this.getResource('/eligibilites-renouvellement');
    }

    public eligibilityEdpTerminalNu(): this {
        return this.getResource('/etalement-de-paiement-terminal-nu');
    }

    public entonnoir(idPanier?: string): this {
        let resourceUrl = '/entonnoir';
        if (idPanier) {
            resourceUrl += '/' + idPanier;
        }
        return this.getResource(resourceUrl);
    }

    public equipementsMobiles(): this {
        return this.getResource('/equipements-mobiles');
    }

    public etalementDePaiementRenouvellement(): this {
        return this.getResource('/etalement-de-paiement-renouvellement');
    }

    public facturation(): this {
        return this.getResource('/adresses-facturation');
    }

    public gcp(): this {
        return this.getResource('/gcp');
    }

    public get<T = any>(): Observable<T> {
        if (this.forceIE11) {
            const timestamp = +new Date();
            this.setParams({
                tsp: timestamp,
            });
        }
        return this.getRequest('GET');
    }

    public getClient(): HttpClient {
        return this.http;
    }

    public getUrl(): string {
        return this._getRequestUrl() + this.resourceUrl;
    }

    public getUserInfo(): ObjectOfString {
        const resource: this = this.getInstance();
        return resource.oauth2.playLoad;
    }

    public iban(): this {
        return this.getResource('/iban');
    }

    public lignes(): this {
        return this.getResource('/lignes');
    }

    public lock(): this {
        return this.getResource('/lock');
    }

    public mettreAjourParcours(): this {
        return this.getResource('/mettre-ajour-parcours');
    }

    public modesLivraison(): this {
        return this.getResource('/modes-livraison');
    }

    public modesPaiement(): this {
        return this.getResource('/modes-paiement');
    }

    public modesFinancement(): this {
        return this.getResource('/modes-financement');
    }

    public courant(nonce: string): this {
        const resource = this.getResource('/courant');
        resource.setParams({ nonce });
        return resource;
    }

    public murProduits(): this {
        return this.getResource('/mur-produits');
    }

    public nd(nd: string): this {
        return this.getResource('/' + nd);
    }

    public numeroTel(): this {
        return this.getResource('/numero-tel');
    }

    public odr(): this {
        return this.getResource('/consulter-odr');
    }

    public offres(id?: number | string): this {
        const resourceUrl = `/offres${id ? '/' + id.toString() : ''}`;
        return this.getResource(resourceUrl);
    }

    public offresAdditionnellesSouscriptibles(): this {
        return this.getResource('/offres-additionnelles-souscriptibles');
    }

    public options(): this {
        return this.getResource('/options');
    }

    public optionsSouscriptibles(): this {
        return this.getResource('/options-souscriptibles');
    }

    public paiement(): this {
        return this.getResource('/paiement');
    }

    public panier(id?: number | string): this {
        const resourceUrl = `/paniers${id ? '/' + id.toString() : ''}`;
        return this.getResource(resourceUrl);
    }

    public parcours(id?: number | string): this {
        const resourceUrl = `/parcours${id ? '/' + id.toString() : ''}`;
        return this.getResource(resourceUrl);
    }

    public personnes(personnesId?: number | string): this {
        let resourceUrl = '/personnes';
        if (personnesId) {
            resourceUrl += '/' + personnesId;
        }
        return this.getResource(resourceUrl);
    }

    public portability(): this {
        return this.getResource('/portabilites');
    }

    public post(body: {}): Observable<any> {
        return this.getRequest('POST', body);
    }

    public postalCode(postalCode: string): this {
        return this.getResource('/' + postalCode);
    }

    public produits(id?: number | string): this {
        const resourceUrl = `/produits${id ? '/' + id : ''}`;
        return this.getResource(resourceUrl);
    }

    public promotions(id?: number): this {
        const resourceUrl = `/promotions${id ? '/' + id : ''}`;
        return this.getResource(resourceUrl);
    }

    public promotionsDisponibles(): this {
        return this.getResource('/promotions-disponibles');
    }

    public eligibilitePromotionnelle(): this {
        return this.getResource('/eligibilite-promotionnelle');
    }

    public prospect(): this {
        return this.getResource('/prospect');
    }

    public prospectWEB(): this {
        return this.getResource('/prospectWEB');
    }

    public ptos(): this {
        return this.getResource('/ptos');
    }

    public put<T = any>(body: { [index: string]: any }): Observable<T> {
        return this.getRequest('PUT', body);
    }

    public referentiel(): this {
        return this.getResource('/referentiel');
    }

    public repriseMobile(): this {
        return this.getResource('/reprise-mobile');
    }

    public ressources(): this {
        return this.getResource('/ressources');
    }

    public scoring(): this {
        return this.getResource('/scoring');
    }

    public session(): this {
        return this.getResource('/session');
    }

    public setLocalService(flag: boolean = true): this {
        const resource: this = this.getInstance();
        resource.localService = flag;
        return resource;
    }

    public setParams(params: { [index: string]: string | number }): this {
        const resource: this = this.getInstance();
        Object.keys(params).forEach((key: string) => {
            resource.params = resource.params.set(key, '' + params[key]);
        });
        return resource;
    }

    public setUrl(resourceUrl: string): this {
        const resource: this = this.getInstance();
        resource.resourceUrl += resourceUrl;
        return resource;
    }

    public techno(techno: string): this {
        return this.getResource('/' + techno);
    }

    public token(): this {
        return this.getResource('/token');
    }

    public useSalesApi(): this {
        const resource: this = this.getInstance();
        resource.headers['x-version'] = this.configService.getData()?.oauth.salesApiVersion || '4';
        return resource;
    }

    public ventes(): this {
        this.setLocalService();
        return this.getResource('/ventes');
    }

    public listerAvantages(): this {
        return this.getResource('/listerAvantages');
    }

    public propositionsCommerciales(): this {
        return this.getResource('/propositions-commerciales');
    }

    public cms(id: string): this {
        const resourceUrl = '/cms/' + id;
        return this.getResource(resourceUrl);
    }

    public produitsPartenaires(idPhone?: string, withDevis: boolean = false): this {
        let resourceUrl = '/produits-partenaires';
        if (idPhone) {
            resourceUrl += '/' + idPhone + (withDevis ? '/devis' : '');
        }
        return this.getResource(resourceUrl);
    }

    protected _getRequestOptions(): {} {
        this.setTrackerId();

        if (
            this.configService.getData()?.environment !== ENV_LIST.PRODUCTION &&
            this.configService.getData()?.environment !== ENV_LIST.LOCAL
        ) {
            this.headers['X-banc'] = this.configService.getData()?.oauth.xBanc;
        }
        if (this.oauth2.access_token) {
            this.headers.Authorization = `Bearer ${this.oauth2.access_token}`;
        }
        return {
            headers: new HttpHeaders(this.headers),
            params: this.params,
        };
    }

    protected _getRequestUrl(): string {
        return this.configService.getData()?.oauth.resourceUrl;
    }

    protected getInstance(): this {
        if (this.inService) {
            return this;
        } else {
            const instance: this = new (this.constructor as any)(
                this.oauth2,
                this.getClient(),
                this.configService,
                this.prismeService,
            ) as this;
            instance.inService = true;
            return instance;
        }
    }

    protected getRequest(httpMethod: string, body?: BasicObject): Observable<any> {
        const http: HttpClient = this.http;
        let obs: Observable<any>;
        switch (httpMethod) {
            case 'POST':
                obs = http.post(this.getUrl(), body, this._getRequestOptions());
                break;

            case 'PUT':
                obs = http.put(this.getUrl(), body, this._getRequestOptions());
                break;

            case 'GET':
                obs = http.get(this.getUrl(), this._getRequestOptions());
                break;

            case 'DELETE':
                obs = http.delete(this.getUrl(), this._getRequestOptions());
                break;

            default:
                throw Error('Unknow HTTP method');
        }

        return obs.pipe(
            catchError((res: any) => {
                if (res.status === Oauth2ResourcesService.HTTP_UNAUTHORIZED) {
                    return this.oauth2.login().pipe(mergeMap(() => obs));
                }

                throw res;
            }),
            publishLast(),
            refCount(),
        );
    }

    protected getResource(resourceUrl: string): this {
        const resource: this = this.getInstance();
        resource.resourceUrl += resourceUrl;
        resource._addSpecialHeader();

        return resource;
    }

    protected setTrackerId(): this {
        this.headers.TrackerId = this.prismeService.getSession();
        return this;
    }

    private _addSpecialHeader(): this {
        const resource: this = this.getInstance();

        const xSource = this.configService.getData()?.oauth.xSource || 'MAGENTO';
        const headers: BasicObject = {
            'X-source': xSource,
            Source: xSource,
            'X-Message-Id': uuidv4(),
        };

        resource.addHeaders(headers);

        return resource;
    }
}
